import { useQuery } from '@tanstack/react-query';
import type { BusyBlock, IsoInstant } from '@swi/contracts';
import { useMemo } from 'react';
import * as api from '../../api/endpoints';
import { dateOnly, timeOnly, weekday } from '../../lib/format';
import { EmptyState, Pill, Skeleton } from '../ui/primitives';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const BLOCK_STYLE: Record<BusyBlock['kind'], { background: string; label: string }> = {
  RESERVATION: { background: 'var(--state-confirmed)', label: 'rezervace' },
  TURNAROUND: { background: 'var(--warn)', label: 'pozemní obsluha' },
  MAINTENANCE: { background: 'var(--danger)', label: 'servis' },
};

/**
 * Day-by-day occupancy strip for one airship.
 *
 * Confirmed reservations are solid, the mandatory ground turnaround around each
 * of them is drawn semi-transparent, and maintenance is hatched — so a renter
 * can see why a slot that looks free is not bookable.
 */
export function AvailabilityCalendar({
  airshipId,
  from,
  to,
  highlight,
}: {
  airshipId: number;
  from: IsoInstant;
  to: IsoInstant;
  highlight?: { from: string; to: string } | null;
}) {
  const calendar = useQuery({
    queryKey: ['calendar', airshipId, from, to],
    queryFn: () => api.getCalendar({ airshipId, from, to }),
  });

  const days = useMemo(() => {
    const start = new Date(new Date(from).setHours(0, 0, 0, 0));
    const end = new Date(to).getTime();
    const result: Date[] = [];
    for (let time = start.getTime(); time < end; time += DAY) {
      result.push(new Date(time));
    }
    return result.slice(0, 21);
  }, [from, to]);

  if (calendar.isLoading) {
    return <Skeleton className="h-48" />;
  }
  if (!calendar.data) {
    return <EmptyState title="Kalendář se nepodařilo načíst" />;
  }

  const blocks = calendar.data.blocks;

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2 text-[11px]">
        {(Object.keys(BLOCK_STYLE) as BusyBlock['kind'][]).map((kind) => (
          <span key={kind} className="inline-flex items-center gap-1.5 text-[var(--text-muted)]">
            <span
              aria-hidden
              className="inline-block size-2.5 rounded-[3px]"
              style={{
                background: BLOCK_STYLE[kind].background,
                opacity: kind === 'TURNAROUND' ? 0.35 : 1,
              }}
            />
            {BLOCK_STYLE[kind].label}
          </span>
        ))}
        <Pill tone="accent">turnaround {String(calendar.data.turnaroundMinutes)} min</Pill>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Hour ruler */}
          <div className="mb-1 flex items-center gap-2">
            <span className="w-24 shrink-0" />
            <div className="relative h-4 flex-1">
              {[0, 6, 12, 18, 24].map((hour) => (
                <span
                  key={hour}
                  className="mono absolute top-0 -translate-x-1/2 text-[10px] text-[var(--text-muted)]"
                  style={{ left: `${String((hour / 24) * 100)}%` }}
                >
                  {String(hour).padStart(2, '0')}
                </span>
              ))}
            </div>
          </div>

          <ul className="flex flex-col gap-1">
            {days.map((day) => {
              const dayStart = day.getTime();
              const dayEnd = dayStart + DAY;
              const dayBlocks = blocks.filter((block) => {
                const blockStart = new Date(block.startAt).getTime();
                const blockEnd = new Date(block.endAt).getTime();
                return blockStart < dayEnd && blockEnd > dayStart;
              });

              const highlightSpan =
                highlight != null
                  ? (() => {
                      const hStart = new Date(highlight.from).getTime();
                      const hEnd = new Date(highlight.to).getTime();
                      if (Number.isNaN(hStart) || Number.isNaN(hEnd) || hEnd <= hStart) {
                        return null;
                      }
                      if (hStart >= dayEnd || hEnd <= dayStart) {
                        return null;
                      }
                      return {
                        left: (Math.max(hStart, dayStart) - dayStart) / DAY,
                        width: (Math.min(hEnd, dayEnd) - Math.max(hStart, dayStart)) / DAY,
                      };
                    })()
                  : null;

              return (
                <li key={dayStart} className="flex items-center gap-2">
                  <span className="mono w-24 shrink-0 text-[11px] text-[var(--text-secondary)]">
                    {weekday(day.toISOString())} {dateOnly(day.toISOString())}
                  </span>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-[var(--surface-glass)]">
                    {/* 6-hour gridlines */}
                    {[6, 12, 18].map((hour) => (
                      <span
                        key={hour}
                        aria-hidden
                        className="absolute inset-y-0 w-px bg-[var(--border-subtle)]"
                        style={{ left: `${String((hour / 24) * 100)}%` }}
                      />
                    ))}

                    {dayBlocks.map((block, index) => {
                      const blockStart = Math.max(new Date(block.startAt).getTime(), dayStart);
                      const blockEnd = Math.min(new Date(block.endAt).getTime(), dayEnd);
                      const style = BLOCK_STYLE[block.kind];
                      return (
                        <span
                          key={`${block.reservationId ?? 'm'}-${block.kind}-${String(index)}`}
                          title={`${style.label}: ${timeOnly(block.startAt)} → ${timeOnly(block.endAt)} — ${block.label}`}
                          className="absolute inset-y-0"
                          style={{
                            left: `${String(((blockStart - dayStart) / DAY) * 100)}%`,
                            width: `${String(Math.max(((blockEnd - blockStart) / DAY) * 100, 0.6))}%`,
                            background: style.background,
                            opacity: block.kind === 'TURNAROUND' ? 0.3 : 0.85,
                            backgroundImage:
                              block.kind === 'MAINTENANCE'
                                ? 'repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 3px, transparent 3px 6px)'
                                : undefined,
                          }}
                        />
                      );
                    })}

                    {highlightSpan !== null && (
                      <span
                        aria-hidden
                        className="absolute inset-y-0 border-2 border-dashed"
                        style={{
                          left: `${String(highlightSpan.left * 100)}%`,
                          width: `${String(Math.max(highlightSpan.width * 100, 0.8))}%`,
                          borderColor: 'var(--accent)',
                          background: 'var(--accent-dim)',
                        }}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {blocks.length === 0 && (
        <p className="mt-3 text-[13px] text-[var(--text-muted)]">
          V tomto období nemá vzducholoď žádnou potvrzenou rezervaci ani servis.
        </p>
      )}
    </div>
  );
}
